import { useCallback, useState } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { logError } from '~publish/legacy/utils/logError'

import { useInsightsSnapshots } from '../context'
import { buildSnapshotsZip, triggerBlobDownload } from '../snapshotZip'

const ZIP_FILENAME = 'buffer-insights-snapshots.zip'

interface UseDownloadSnapshotsResult {
  downloadSnapshots: () => Promise<void>
  isPreparing: boolean
  canDownload: boolean
}

/**
 * Header action for "Download Snapshots": packages the registered section
 * snapshots into one ZIP. Fully client-side — it never touches the paid backend
 * export path, and it captures sequentially and aborts the whole download if any
 * section fails.
 */
export function useDownloadSnapshots(): UseDownloadSnapshotsResult {
  const { snapshots, canDownload } = useInsightsSnapshots()
  const [isPreparing, setIsPreparing] = useState(false)

  const downloadSnapshots = useCallback(async (): Promise<void> => {
    if (isPreparing || snapshots.length === 0) return
    setIsPreparing(true)
    try {
      const zipBlob = await buildSnapshotsZip(snapshots)
      triggerBlobDownload(zipBlob, ZIP_FILENAME)
    } catch (error) {
      logError(error, { context: 'downloadInsightsSnapshots' })
      toast.error('Failed to prepare images. Please try again.')
    } finally {
      setIsPreparing(false)
    }
  }, [isPreparing, snapshots])

  return { downloadSnapshots, isPreparing, canDownload }
}
